import { dateRange, escapeHtml, fullName, locationText, mdToHtml, type TailoredResume } from "./markdown";

export const meta = { id: "modern", name: "Modern" };

const CSS = `
  :root { --ink: #1a1a1a; --muted: #555; --faint: #777; --rule: #cfcfcf; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #ededf0; color: var(--ink);
    font-family: "Helvetica Neue", Arial, "Segoe UI", system-ui, sans-serif; }
  .page { max-width: 8.5in; margin: 24px auto; background: #fff; padding: 0.7in 0.8in;
    box-shadow: 0 1px 6px rgba(0,0,0,0.14); }
  header { margin-bottom: 6px; }
  h1 { font-size: 27px; font-weight: 700; margin: 0; }
  .title { font-size: 14px; color: var(--muted); margin: 4px 0 10px; font-weight: 500; }
  .contact { font-size: 11px; color: var(--muted); line-height: 1.6; }
  section { margin-top: 20px; }
  h2 { font-size: 12px; text-transform: uppercase; font-weight: 700;
    color: var(--ink); margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid var(--rule); }
  .body { font-size: 12px; line-height: 1.45; }
  .body p { margin: 0 0 6px; }
  .body ul { margin: 5px 0 8px; padding-left: 16px; }
  .body li { margin: 3px 0; }
  .entry { margin-bottom: 14px; }
  .entry:last-child { margin-bottom: 0; }
  .row { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
  .role { font-weight: 700; font-size: 12.5px; }
  .org { color: var(--muted); font-weight: 500; font-size: 11.5px; margin-top: 1px; }
  .meta { color: var(--faint); font-size: 10.5px; white-space: nowrap; }
  .rel { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
  .rel ul { display: inline; margin: 0; padding: 0; }
  .rel li { display: inline; }
  .rel li:not(:last-child)::after { content: " · "; }
  .rel .label { font-weight: 700; color: var(--ink); }
  .chips { display: flex; flex-wrap: wrap; gap: 6px 8px; font-size: 11px; }
  .ref { margin-bottom: 9px; font-size: 11px; line-height: 1.4; }
  .ref .name { font-weight: 700; }
  .ref .sub { color: var(--muted); }
  @media print {
    body { background: #fff; }
    .page { box-shadow: none; margin: 0; max-width: none; padding: 0.4in 0.5in; }
  }
`;

const has = (v: unknown) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
const section = (title: string, inner: string) =>
	has(inner) ? `<section><h2>${title}</h2><div class="body">${inner}</div></section>` : "";

function header(r: TailoredResume) {
	const c = (r.contact || {}) as TailoredResume["contact"];
	const bits = [c.email, c.phone, locationText(c.location)].filter(Boolean).map(escapeHtml).join("  |  ");
	return `<header>
      <h1>${escapeHtml(fullName(c))}</h1>
      ${c.title ? `<div class="title">${escapeHtml(c.title)}</div>` : ""}
      <div class="contact">${bits}</div>
    </header>`;
}

const experience = (list: any[] | undefined) =>
	(list || [])
		.map(
			(j) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(j.jobTitle || "")}</span><span class="meta">${escapeHtml(dateRange(j))}</span></div>
      <div class="row"><span class="org">${escapeHtml(j.employer || "")}</span><span class="meta">${escapeHtml(locationText(j.location))}</span></div>
      ${has(j.highlights) ? mdToHtml(j.highlights) : ""}
      ${has(j.relevantSkills) ? `<div class="rel"><span class="label">Skills:</span> ${mdToHtml(j.relevantSkills)}</div>` : ""}
    </div>`,
		)
		.join("");

const education = (list: any[] | undefined) =>
	(list || [])
		.map(
			(e) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(e.educationLevel || e.fieldOfStudy || "")}</span><span class="meta">${escapeHtml(e.graduation || "")}</span></div>
      <div class="row"><span class="org">${escapeHtml(e.schoolName || "")}</span><span class="meta">${escapeHtml(locationText(e.location))}</span></div>
      ${has(e.details) ? mdToHtml(e.details) : ""}
    </div>`,
		)
		.join("");

const languages = (list: any[] | undefined) =>
	`<div class="chips">${(list || []).map((l) => `<span>${escapeHtml(l.language)}${l.proficiency ? ` — ${escapeHtml(l.proficiency)}` : ""}</span>`).join("")}</div>`;

const references = (list: any[] | undefined) =>
	(list || [])
		.map(
			(r) => `<div class="ref">
      <span class="name">${escapeHtml(r.name || "")}</span>${r.designation ? `, ${escapeHtml(r.designation)}` : ""}${r.workPlace ? ` at ${escapeHtml(r.workPlace)}` : ""}<br />
      <span class="sub">${[locationText(r.location), r.phone, r.email].filter(Boolean).map(escapeHtml).join(" · ")}</span>
    </div>`,
		)
		.join("");

const customSections = (list: any[] | undefined) =>
	(list || []).map((s) => section(escapeHtml(s.title), has(s.content) ? mdToHtml(s.content) : "")).join("");

const linksList = (list: any[] | undefined) =>
	(list || []).map((l) => `<div>${l.type ? escapeHtml(l.type) + ": " : ""}${escapeHtml(l.value)}</div>`).join("");

export function renderBody(r: TailoredResume): string {
	return [
		header(r),
		section("Summary", has(r.summary) ? mdToHtml(r.summary) : ""),
		section("Skills", has(r.skills) ? mdToHtml(r.skills) : ""),
		section("Experience", experience(r.workExperience)),
		section("Education", education(r.education)),
		section("Certifications", has(r.certifications) ? mdToHtml(r.certifications) : ""),
		customSections(r.customSections),
		section("Accomplishments", has(r.accomplishments) ? mdToHtml(r.accomplishments) : ""),
		section("Affiliations", has(r.affiliations) ? mdToHtml(r.affiliations) : ""),
		section("Languages", has(r.languages) ? languages(r.languages) : ""),
		section("Additional Information", has(r.additionalInfo) ? mdToHtml(r.additionalInfo) : ""),
		section("Links", has(r.personalLinks) ? linksList(r.personalLinks) : ""),
		section("References", references(r.references)),
	].join("");
}

export function renderDocument(resume: TailoredResume): string {
	return `<!doctype html><html><head><meta charset="utf-8" /><style>${CSS}</style></head><body><div class="page" id="resume">${renderBody(resume)}</div></body></html>`;
}
